/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import type { ApiModule, ApiUrls } from '@opengeoweb/api';
import { ErrorBoundary } from '@opengeoweb/shared';
import { Bulletin } from '../Bulletin';
import { Layout } from '../Layout';
import Notifications from '../Notifications/Notifications';
import { NotificationTrigger } from '../NotificationTrigger/NotificationTrigger';
import { NotificationTriggerProvider } from '../NotificationTrigger/NotificationTriggerContext';
import { TimeSeries } from '../TimeSeries';

const spaceweatherViewPreset = {
  title: 'Spaceweather module',
  id: 'spaceweather',
  componentType: 'SpaceweatherModule',
  initialProps: {},
};

type SpaceweatherComponentType = 'SpaceweatherModule';

export interface ComponentsLookUpPayload {
  componentType: SpaceweatherComponentType;
  id: string;
  title?: string;
  initialProps: Record<string, never>;
}

// base type for connected modules
export interface ApiModuleProps extends ApiModule {
  config: ApiUrls;
}

export const SpaceweatherWrapper: React.FC = () => (
  <NotificationTriggerProvider>
    <NotificationTrigger />
    <ErrorBoundary>
      <Layout
        leftBottom={<Notifications />}
        leftTop={<Bulletin />}
        right={<TimeSeries />}
      />
    </ErrorBoundary>
  </NotificationTriggerProvider>
);

export const componentsLookUp = (
  payload: ComponentsLookUpPayload,
): React.ReactElement => {
  const { componentType } = payload;
  switch (componentType) {
    case spaceweatherViewPreset.componentType:
      return <SpaceweatherWrapper data-testid="spaceweathermodule" />;
    default:
      return null!;
  }
};
